import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight } from 'lucide-react';
import { INSIGHTS } from '../mock/mock';
import usePageMeta from '../hooks/usePageMeta';

const Section = ({ children, className = '' }) => (
  <section className={`max-w-[1400px] mx-auto px-6 lg:px-10 ${className}`}>{children}</section>
);

export default function Insights() {
  usePageMeta({
    title: 'Insights — Perspectives on Leadership, Architecture & Execution',
    description: 'Perspectives on fractional leadership, enterprise architecture, and disciplined execution from the Waypoint Transformation team.',
    path: '/insights',
  });
  const [featured, ...rest] = INSIGHTS;
  return (
    <div>
      <section className="bg-[#F5F7FA]">
        <Section className="pt-20 pb-16 lg:pt-28 lg:pb-20">
          <div className="text-[11px] uppercase tracking-[0.24em] text-slate-500 mb-6">Insights</div>
          <h1 className="font-serif text-5xl lg:text-7xl leading-[1.02] tracking-[-0.02em] text-[#0F2A44] max-w-4xl text-balance">
            Perspectives on leadership, architecture, and disciplined execution.
          </h1>
        </Section>
      </section>

      {/* Featured */}
      <Section className="py-20">
        <Link to={`/insights/${featured.id}`} className="group grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <div className="relative aspect-[16/10] overflow-hidden rounded-sm">
              <img src={featured.image} alt={featured.title} className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
            </div>
          </div>
          <div className="lg:col-span-5">
            <div className="text-[11px] uppercase tracking-[0.2em] text-slate-500 mb-4">{featured.category} · Featured</div>
            <h2 className="font-serif text-3xl lg:text-5xl leading-[1.08] text-[#0F2A44] group-hover:text-[#3E6A94] transition-colors">{featured.title}</h2>
            <p className="mt-5 text-slate-600 leading-relaxed text-lg">{featured.excerpt}</p>
            <div className="mt-6 flex items-center gap-4 text-xs text-slate-500">
              <span>{featured.author}</span><span>•</span><span>{featured.date}</span><span>•</span><span>{featured.readTime}</span>
            </div>
            <div className="mt-8 inline-flex items-center gap-2 text-sm text-[#0F2A44] border-b border-[#0F2A44]/40 pb-1 group-hover:border-[#0F2A44] transition-colors">
              Read article <ArrowUpRight className="w-4 h-4" />
            </div>
          </div>
        </Link>
      </Section>

      {/* Grid */}
      <Section className="pb-24 lg:pb-32">
        <div className="border-t border-slate-200 pt-12">
          <div className="grid md:grid-cols-2 gap-x-10 gap-y-14">
            {rest.map((post) => (
              <Link key={post.id} to={`/insights/${post.id}`} className="group">
                <div className="relative aspect-[16/10] overflow-hidden rounded-sm mb-6">
                  <img src={post.image} alt={post.title} className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                </div>
                <div className="text-[11px] uppercase tracking-[0.2em] text-slate-500 mb-3">{post.category}</div>
                <h3 className="font-serif text-2xl lg:text-3xl text-[#0F2A44] leading-snug group-hover:text-[#3E6A94] transition-colors">{post.title}</h3>
                <p className="mt-4 text-slate-600 leading-relaxed">{post.excerpt}</p>
                <div className="mt-4 text-xs text-slate-500">{post.date} · {post.readTime}</div>
              </Link>
            ))}
          </div>
        </div>
      </Section>
    </div>
  );
}
